import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  isMenuSection,
  type MenuDefinition,
  type MenuGroup,
  type MenuItem,
} from '@/shared/layouts/menu/types';

/**
 * RAH sidebar — MN-01..MN-11 of `_menu-design-rule.md`.
 *
 * 106px icon rail: brand + role badge, 一般 / 管理 tabs, one row per category,
 * children in a flyout anchored to the clicked row.
 *
 * The markup is the ConceptDesign's own (`shell/rah-shell.js`), class for class,
 * because `styles/rah-shell.scss` is that prototype's stylesheet: `.menu-tabs
 * button`, `.flyout` + `.flyout-arrow` + `#flyoutContent`, `.flyout-overlay`.
 * antd's `Segmented` and `Popover` were tried here and neither can wear those
 * rules — `Segmented` renders labels, not buttons, and `Popover` portals to
 * `document.body`, which is outside `.rah-shell`, where none of the scoped CSS
 * reaches. Hence the hand-rolled flyout below.
 */

export interface RahSidebarProps {
  menu: MenuDefinition;
  /** Role pill above the user name (MN-10 / R-02), e.g. `オーナー`. */
  roleLabel: string;
  userName: string;
  /** Two-line sidebar footer (MN-11). */
  footerLines?: string[];
  /** Prefix every in-app path with `/{tenant}`. */
  tenantPrefix: string;
}

/** Gap the flyout keeps from the viewport edge when it has to slide up. */
const VIEWPORT_MARGIN = 16;

const normalize = (path: string) => path.replace(/\/+$/, '') || '/';

export const RahSidebar: React.FC<RahSidebarProps> = ({
  menu,
  roleLabel,
  userName,
  footerLines = [],
  tenantPrefix,
}) => {
  const location = useLocation();
  const currentPath = normalize(location.pathname);

  const resolvePath = useCallback(
    (path: string) => (path === '/' ? tenantPrefix || '/' : `${tenantPrefix}${path}`),
    [tenantPrefix]
  );

  /**
   * How well a menu path owns the current URL: 0 for no, otherwise the length
   * of the match so the most specific entry wins.
   *
   * Prefix, not equality. RA2N screens drill into their own sub-routes —
   * `/visits/schedule/:massagerId/:visitdate`,
   * `/rezepts/visit_check_ready/:ids/:yearmonth` — and an exact test drops the
   * highlight the moment you open a row, leaving the rail blank on a page the
   * menu plainly owns.
   *
   * The boundary is a `/`, so `/rezepts/init` never claims `/rezepts/initial`.
   * The dashboard is exact-only: its path is `/`, which resolves to the tenant
   * root and would otherwise prefix every URL in the portal.
   */
  const matchScore = useCallback(
    (path: string) => {
      const target = normalize(resolvePath(path));
      if (target === currentPath) return target.length;
      if (path !== '/' && currentPath.startsWith(`${target}/`)) return target.length;
      return 0;
    },
    [currentPath, resolvePath]
  );

  /** The tab + category that own the current URL, so the rail marks itself. */
  const activeContext = useMemo(() => {
    const candidates = menu.tabs.flatMap(tab =>
      tab.groups.flatMap(group =>
        [
          ...(group.path ? [group.path] : []),
          ...(group.items ?? []).flatMap(entry => (isMenuSection(entry) ? [] : [entry.path])),
        ].map(path => ({ tabId: tab.id, categoryId: group.id, score: matchScore(path) }))
      )
    );

    return candidates.reduce<(typeof candidates)[number] | null>(
      (best, candidate) => (candidate.score > (best?.score ?? 0) ? candidate : best),
      null
    );
  }, [menu, matchScore]);

  const [activeTabId, setActiveTabId] = useState(() => activeContext?.tabId ?? menu.tabs[0]?.id);
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  /** Viewport rect of the row the open flyout hangs off. */
  const [anchorTop, setAnchorTop] = useState(0);
  const [placement, setPlacement] = useState({ top: 0, arrowTop: 18 });

  const flyoutRef = useRef<HTMLDivElement>(null);

  const closeFlyout = useCallback(() => setOpenCategoryId(null), []);

  // Following a link lands on a new URL: adopt its tab and drop the flyout.
  useEffect(() => {
    closeFlyout();
    if (activeContext?.tabId) setActiveTabId(activeContext.tabId);
  }, [activeContext?.tabId, currentPath, closeFlyout]);

  // Escape closes the flyout wherever focus is (rah-shell.js does the same).
  useEffect(() => {
    if (!openCategoryId) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeFlyout();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openCategoryId, closeFlyout]);

  /**
   * Park the flyout beside its row, sliding it up only when it would run off
   * the bottom; the arrow then walks back down to stay pointed at the row.
   * Measured before paint so the panel never appears in the wrong place.
   */
  useLayoutEffect(() => {
    if (!openCategoryId) return;
    const height = flyoutRef.current?.offsetHeight ?? 0;
    let top = anchorTop;
    if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
      top = Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);
    }
    setPlacement({
      top,
      arrowTop: Math.min(height - 26, Math.max(10, anchorTop - top + 18)),
    });
    flyoutRef.current?.querySelector<HTMLElement>('.flyout-item')?.focus();
  }, [openCategoryId, anchorTop]);

  const activeTab = menu.tabs.find(t => t.id === activeTabId) ?? menu.tabs[0];
  const openGroup = activeTab?.groups.find(g => g.id === openCategoryId);

  const toggleCategory = (group: MenuGroup, el: HTMLElement) => {
    if (openCategoryId === group.id) {
      closeFlyout();
      return;
    }
    setAnchorTop(el.getBoundingClientRect().top);
    setOpenCategoryId(group.id);
  };

  const renderCategory = (group: MenuGroup) => {
    const hasChildren = !!group.items?.length;
    const isOpen = openCategoryId === group.id;
    // While a flyout is open it owns the highlight; otherwise the page does.
    const isActive = openCategoryId ? isOpen : activeContext?.categoryId === group.id;

    const className = [
      'nav-category',
      hasChildren ? 'has-children' : '',
      isActive ? 'active' : '',
      group.disabled ? 'is-disabled' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const inner = (
      <>
        <span className="nav-ico">{group.icon}</span>
        <span className="nav-label">{group.label}</span>
        {group.badge !== undefined && <span className="nav-badge">{group.badge}</span>}
        {hasChildren && <span className="nav-arrow">▶</span>}
      </>
    );

    if (hasChildren) {
      return (
        <a
          key={group.id}
          className={className}
          role="button"
          tabIndex={0}
          title={group.label}
          aria-haspopup="true"
          aria-expanded={isOpen}
          onClick={e => toggleCategory(group, e.currentTarget)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              toggleCategory(group, e.currentTarget);
            }
          }}
        >
          {inner}
        </a>
      );
    }

    if (!group.path || group.disabled) {
      return (
        <a key={group.id} className={className} aria-disabled title={group.label}>
          {inner}
        </a>
      );
    }

    return (
      <Link key={group.id} className={className} to={resolvePath(group.path)} title={group.label}>
        {inner}
      </Link>
    );
  };

  const renderFlyoutItem = (item: MenuItem, index: number) => {
    const target = resolvePath(item.path);
    // Same rule as the rail, so a child stays marked on its own sub-routes.
    const isActive = matchScore(item.path) > 0;
    const className = ['flyout-item', isActive ? 'active' : '', item.disabled ? 'is-disabled' : '']
      .filter(Boolean)
      .join(' ');

    const inner = (
      <>
        <span className="fi-ico">{item.icon}</span>
        <span className="fi-name">{item.label}</span>
        {item.badge !== undefined && <span className="fi-badge">{item.badge}</span>}
        {item.tag && <span className={`fi-tag${item.tag === 'AI' ? ' ai' : ''}`}>{item.tag}</span>}
      </>
    );

    if (item.disabled) {
      return (
        <span key={index} className={className} aria-disabled title={`${item.label}（未実装）`}>
          {inner}
        </span>
      );
    }

    if (item.openInNewTab || item.externalUrl) {
      return (
        <a
          key={index}
          className={className}
          href={item.externalUrl ?? item.path}
          target="_blank"
          rel="noopener noreferrer"
          title={item.label}
        >
          {inner}
        </a>
      );
    }

    return (
      <Link key={index} className={className} to={target} title={item.label} onClick={closeFlyout}>
        {inner}
      </Link>
    );
  };

  const switchTab = (tabId: string) => {
    closeFlyout();
    setActiveTabId(tabId);
  };

  return (
    <>
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <div className="brand-logo-wrap">
              <img src="/images/hitowa/keilog_icon.png" alt="" />
            </div>
            <img className="brand-wordmark" src="/images/hitowa/keilog_text.png" alt="KEiLOG" />
          </div>
          <div className="brand-role" title="ログインユーザー">
            <div className="user-avatar">{roleLabel}</div>
            <div className="user-name">{userName}</div>
          </div>
        </div>

        {menu.tabs.length > 1 && (
          <div className="menu-tabs">
            {menu.tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={tab.id === activeTabId ? 'active' : undefined}
                onClick={() => switchTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <nav className="nav-list">{activeTab?.groups.map(renderCategory)}</nav>

        {footerLines.length > 0 && (
          <div className="sidebar-footer">
            {footerLines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </div>
        )}
      </aside>

      {/* MN-07. Sits next to the rail, not in a portal: `rah-shell.scss` scopes
          every rule under `.rah-shell`, so the panel has to stay inside it. */}
      <div
        className={`flyout-overlay${openGroup ? ' open' : ''}`}
        onClick={closeFlyout}
        aria-hidden
      />
      {openGroup && (
        <div className="flyout" ref={flyoutRef} style={{ top: placement.top }}>
          <div className="flyout-arrow" style={{ top: placement.arrowTop }} />
          <div id="flyoutContent">
            <div className="flyout-head">
              <div className="fh-ico">{openGroup.icon}</div>
              <div className="fh-name">{openGroup.label}</div>
            </div>
            {openGroup.items?.map((entry, index) =>
              isMenuSection(entry) ? (
                <div key={index} className="flyout-section">
                  {entry.section}
                </div>
              ) : (
                renderFlyoutItem(entry, index)
              )
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RahSidebar;
